import React, { useEffect, useState } from "react";
import {
  Box,
  Menu,
  Grid,
  Modal,
  Table,
  Stack,
  Dialog,
  TableRow,
  MenuItem,
  Collapse,
  TableBody,
  TableCell,
  TableHead,
  TextField,
  IconButton,
  Pagination,
  Typography,
  DialogTitle,
  DialogContent,
  DialogActions,
  TableContainer,
  CircularProgress,
  MenuItem as MuiMenuItem,
} from "@mui/material";
import {
  Search,
  ExpandLess,
  ExpandMore,
  FilterList,
  Assessment as AssessmentIcon,
  AttachFile as AttachFileIcon,
} from "@mui/icons-material";
import "moment-timezone";
import moment from "moment";
import Swal from "sweetalert2";
import { CSVLink } from "react-csv";
import commaNumber from "comma-number";
import { useNavigate } from "react-router-dom";
import CloseIcon from "@mui/icons-material/Close";
import GetAppIcon from "@mui/icons-material/GetApp";
import { ThemeProvider } from "@mui/material/styles";
import { errorMsg } from "../../../../utils/toast-msg";
import EditMenu from "../../../../assets/image/Frame.svg";
import { errorAlert } from "../../../../utils/error-alert";
import { deleteAlert } from "../../../../utils/delete-alert";
import { successAlert } from "../../../../utils/success-alert";
import ResetIcon from "../../../../assets/image/reseticon.svg";
import PrintIcon from "../../../../assets/image/PrintButton.svg";
import { CustomInput } from "../../../../components/CustomInput";
import { CustomSelect } from "../../../../components/CustomSelect";
import { CustomMenuItem } from "../../../../components/CustomMenuItem";
import { PaginationTheme } from "../../../../components/PaginationTheme";
import { CustomSearchInput } from "../../../../components/CustomSearchInput";
import { CustomAutocomplete } from "../../../../components/CustomAutocomplete";
import { CustomPaginationSelect } from "../../../../components/CustomPaginationSelect";
import { getCompanyBankAccounts } from "../../../../services/company/company-bank-account";
import {
  deleteHajjPackage,
  getHajjPackage,
  getHajjPackages,
} from "../../../../services/hajj/hajj-package";
import Transactionicon from "../../../../assets/image/Transactionicon.svg";
import BackButton from "../../../../components/BackButton/BackButton";

const modalStyle = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  bgcolor: "background.paper",
  boxShadow: 24,
  borderRadius: "12px",
};

// ---------------------------------------------------------------------------
// Service-wise report helpers
// ---------------------------------------------------------------------------

const SERVICES = ["VISA", "TICKET", "TRANSPORT", "HOTEL", "FOOD", "OTHER", "ALL"];

/**
 * Builds a service-wise breakdown of Vendor Payment, User Payment, and
 * Profit/Loss from a package's raw transaction list.
 *
 * Vendor Payment for a service = sum(debit + creditUsed - credit) for rows
 *   where ledgerType is VENDOR and service matches.
 * User Payment for a service   = sum(debit + creditUsed - credit) for rows
 *   where ledgerType is USER and service matches.
 * Profit/Loss for a service    = User Payment - Vendor Payment.
 *
 * Any transaction whose `service` doesn't match one of the known SERVICES
 * values is bucketed into "OTHER" rather than silently dropped.
 */
const calcServiceWiseReport = (transactions) => {
  const buckets = SERVICES.reduce((acc, service) => {
    acc[service] = { service, vendorPayment: 0, userPayment: 0 };
    return acc;
  }, {});

  (transactions || []).forEach((row) => {
    const rawService = (row.transactionType || "").toUpperCase();


    const service = SERVICES.includes(rawService) ? rawService : "OTHER";
    const ledgerType = (row.ledgerType || "").toUpperCase();

    const amount =
      Number(row.debit ?? 0) + Number(row.creditUsed ?? 0) - Number(row.credit ?? 0);

    if (ledgerType.includes("VENDOR")) {
      buckets[service].vendorPayment += amount;
    } else if (ledgerType.includes("USER")) {
      buckets[service].userPayment += amount;
    }
  });

  const rows = SERVICES.map((service) => {
    const { vendorPayment, userPayment } = buckets[service];
    return {
      service,
      vendorPayment,
      userPayment,
      profitLoss: userPayment - vendorPayment,
    };
  });

  const totalVendorPayment = rows.reduce((s, r) => s + r.vendorPayment, 0);
  const totalUserPayment = rows.reduce((s, r) => s + r.userPayment, 0);
  const totalProfitLoss = rows.reduce((s, r) => s + r.profitLoss, 0);

  return { rows, totalVendorPayment, totalUserPayment, totalProfitLoss };
};

const HajjPackageList = () => {
  const navigate = useNavigate();

  const [show, setShow] = useState({});
  const [data, setData] = useState({});
  const [csvData, setCsvData] = useState([]);
  const [charges, setCharges] = useState([]);
  const [searchData, setSearchData] = useState([]);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [limit, setLimit] = useState(20);
  const [pageCount, setPageCount] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [anchorEl, setAnchorEl] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [searchInput, setSearchInput] = useState("");

  // --- Service-wise report modal state ---
  const [reportOpen, setReportOpen] = useState(false);
  const [reportLoading, setReportLoading] = useState(false);
  const [reportPackageName, setReportPackageName] = useState("");
  const [reportRows, setReportRows] = useState([]);
  const [reportTotals, setReportTotals] = useState({
    totalVendorPayment: 0,
    totalUserPayment: 0,
    totalProfitLoss: 0,
  });


  useEffect(() => {
    (async () => {
      try {
        const query = {
          limit,
          page: pageNumber,
          searchInput,
        };

        const {
          data: {
            payload: { data: charges, meta },
          },
        } = await getHajjPackages(query);

        setCsvData(charges);
        setCharges(charges);
        setSearchData(charges);
        setPageCount(meta?.totalPages);
      } catch (error) {
        if (error?.response?.status === 403) {
          errorMsg("You have no permission to access this page");
        } else {
          console.error("An error occurred while fetching data:", error);
          errorMsg("An unexpected error occurred. Please try again later.");
        }
      }
    })();
  }, [limit, pageNumber, searchInput]);

  const handleShow = ({ type, payload }) => {
    setData(payload);
    setShow({ ...show, [type]: true });
  };

  const handleClose = (type) => {
    setData({});
    setShow({ ...show, [type]: false });
  };

  const handleDelete = async () => {
    setShow({});
    setAnchorEl(false);

    deleteAlert("Are you sure you want to delete this package?").then(
      async (result) => {
        if (result.isConfirmed) {
          const result = await deleteHajjPackage(data.id);
          if (result.error) errorAlert(result.error.data.message);
          setCsvData(csvData.filter((item) => item.id != data.id));
          setSearchData(searchData.filter((item) => item.id != data.id));
          setCharges(charges.filter((item) => item.id != data.id));
          setData({});
          successAlert("Hajj Package deleted successfully");
        }
      }
    );
  };

  const handelSearchItems = (e) => {
    const value = e.target.value;
    setSearchInput(value);
    setPageNumber(1);
  };

  const handlePageChange = (event, value) => {
    setPageNumber(value);
  };

  const handlePageView = (value) => {
    setLimit(value);
  };

  const handleExpandClick = (id) => {
    setExpanded((prev) => (prev === id ? null : id));
  };

  // ---------------------------------------------------------------------------
  // Service-wise Vendor/User payment + Profit/Loss report
  // ---------------------------------------------------------------------------

  const handleOpenServiceReport = async (row) => {
    setReportOpen(true);
    setReportLoading(true);
    setReportPackageName(row?.hajjPackageName || "");

    try {
      const { data } = await getHajjPackage(row.id);
      const booking = data?.payload;
      const transactions = booking?.transactions || [];

      const { rows, totalVendorPayment, totalUserPayment, totalProfitLoss } =
        calcServiceWiseReport(transactions);

      setReportRows(rows);
      setReportTotals({ totalVendorPayment, totalUserPayment, totalProfitLoss });
    } catch (error) {
      console.error(error);
      errorMsg("Failed to load service-wise report.");
      setReportRows([]);
      setReportTotals({ totalVendorPayment: 0, totalUserPayment: 0, totalProfitLoss: 0 });
    } finally {
      setReportLoading(false);
    }
  };

  const handleCloseServiceReport = () => {
    setReportOpen(false);
    setReportRows([]);
    setReportPackageName("");
    setReportTotals({ totalVendorPayment: 0, totalUserPayment: 0, totalProfitLoss: 0 });
  };


  return (
    <Box sx={{ marginTop: { xs: "20px", sm: "0", md: "0" } }}>
      <BackButton />
      {/* Table Section */}
      <Box
        sx={{
          padding: "16px",
          backgroundColor: "var(--color-bg-white)",
          borderRadius: "12px",
        }}
      >
        {/* Top Bar */}
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            justifyContent: { xs: "flex-start", sm: "space-between" },
            alignItems: { xs: "flex-start", sm: "center" },
            borderRadius: "12px",
            gap: { xs: 2, sm: 0 },
          }}
          mb={2}
        >
          {/* Left Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "column", sm: "row" },
              alignItems: "center",
              gap: "12px",
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {/* Quick Search */}
            <Box
              sx={{
                position: "relative",
                width: { xs: "100%", sm: "auto" },
              }}
              className={"placeholderColor"}
            >
              <Search
                sx={{
                  position: "absolute",
                  top: "7px",
                  left: "5px",
                  color: "var(--color-draft)",
                }}
              />
              <CustomSearchInput
                onChange={handelSearchItems}
                type="text"
                placeholder="Quick Search"
                sx={{
                  padding: "0px 32px",
                  backgroundColor: "var(--color-bg-hover-sitebar)",
                  width: "100%",
                }}
              />
            </Box>

            {/* View Dropdown */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "var(--color-bg-hover-sitebar)",
                borderRadius: "4px",
                padding: "5px 5px",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "var(--color-draft)",
                  paddingRight: "10px",
                }}
              >
                View
              </Typography>
              <CustomPaginationSelect
                onChange={(e) => handlePageView(e.target.value)}
                defaultValue={10}
                sx={{
                  backgroundColor: "var(--color-bg-white)",
                  height: "25px",
                  border: "none",
                  color: "var(--color-draft)",
                  width: "100%",
                }}
              >
                <MenuItem value={10}>10</MenuItem>
                <MenuItem value={20}>20</MenuItem>
                <MenuItem value={50}>50</MenuItem>
                <MenuItem value={100}>100</MenuItem>
              </CustomPaginationSelect>
            </Box>

            {/* Filter */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "var(--color-bg-hover-sitebar)",
                borderRadius: "4px",
                padding: "5px 5px",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "var(--color-draft)",
                  paddingRight: "10px",
                }}
              >
                Filter
              </Typography>
              <FilterList
                sx={{
                  color: "var(--color-draft)",
                  backgroundColor: "var(--color-bg-white)",
                  padding: "2px 4px",
                  fontSize: "22px",
                  borderRadius: "5px",
                }}
              />
            </Box>

            {/* Reset */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                backgroundColor: "var(--color-bg-hover-sitebar)",
                borderRadius: "4px",
                padding: "5px 5px",
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Typography
                sx={{
                  fontFamily: "Outfit",
                  fontWeight: 400,
                  fontSize: "14px",
                  color: "var(--color-draft)",
                  paddingRight: "10px",
                }}
              >
                Reset
              </Typography>
              <img
                src={ResetIcon}
                alt="Reset"
                style={{
                  width: "16px",
                  height: "16px",
                  backgroundColor: "var(--color-bg-white)",
                  padding: "6px 6px",
                  borderRadius: "5px",
                }}
              />
            </Box>
          </Box>

          {/* Right Section */}
          <Box
            sx={{
              display: "flex",
              flexDirection: { xs: "row", sm: "row" },
              gap: "12px",
              alignItems: "center",
              justifyContent: { xs: "flex-start", sm: "space-between" },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {/* Add Party Button */}
            <button
              style={{
                backgroundColor: "var(--color-primary-dark)",
                color: "var(--color-bg-white)",
                borderRadius: "5px",
                padding: "10px 12px",
                fontFamily: "Outfit",
                fontSize: "13px",
                fontWeight: 500,
                textTransform: "none",
                boxShadow: "none",
                border: "none",
                display: "flex",
                gap: "5px",
                alignItems: "center",
                cursor: "pointer",
              }}
              onClick={() => navigate("/dashboard/hajj-package-create/new")}
            >
              Add Hajj Package
            </button>

            {/* Print CSV */}
            <CSVLink data={csvData}>
              <img
                src={PrintIcon}
                alt="Print"
                style={{
                  height: "35px",
                  cursor: "pointer",
                }}
              />
            </CSVLink>
          </Box>
        </Box>

        <TableContainer sx={{ backgroundColor: "var(--color-bg-white)" }}>
          <Table
            sx={{
              minWidth: "900px",
              tableLayout: "auto",
              backgroundColor: "var(--color-white)",
            }}
          >
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: "var(--color-primary-light)",
                  color: "var(--color-bg-white)",
                  fontWeight: "500",
                }}
              >
                <TableCell sx={{ padding: "4px", textAlign: "center" }} />
                <TableCell
                  sx={{
                    textAlign: "center",
                    color: "var(--color-bg-white)",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                    padding: "4px",
                  }}
                >
                  Package ID
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    color: "var(--color-bg-white)",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                    padding: "4px",
                  }}
                >
                  Package Type
                </TableCell>

                <TableCell
                  sx={{
                    textAlign: "center",
                    color: "var(--color-bg-white)",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                    padding: "4px",
                  }}
                >
                  Package Name
                </TableCell>

                <TableCell
                  sx={{
                    textAlign: "center",
                    color: "var(--color-bg-white)",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                    padding: "4px",
                  }}
                >
                  Departure Date
                </TableCell>

                <TableCell
                  sx={{
                    textAlign: "center",
                    color: "var(--color-bg-white)",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                    padding: "4px",
                  }}
                >
                  Return Date
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    color: "var(--color-bg-white)",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                    padding: "4px",
                  }}
                >
                  Total Days
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    color: "var(--color-bg-white)",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                    padding: "4px",
                  }}
                >
                  Total Persons
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    color: "var(--color-bg-white)",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                    padding: "4px",
                  }}
                >
                  Status
                </TableCell>
                <TableCell
                  sx={{
                    textAlign: "center",
                    color: "var(--color-bg-white)",
                    fontWeight: 400,
                    fontFamily: "Outfit",
                    padding: "4px",
                  }}
                >
                  Actions
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {searchData?.map((row) => (
                <React.Fragment key={row.id}>
                  <TableRow
                    key={row.id}
                    sx={{
                      backgroundColor:
                        expanded === row.id
                          ? "var(--color-bg-hover-sitebar)"
                          : "var(--color-bg-white)",
                    }}
                  >
                    <TableCell sx={{ textAlign: "center", padding: "4px" }}>
                      <IconButton
                        sx={{
                          color: "var(--color-draft)",
                          border: "solid var(--color-draft) 1px",
                          width: "18px",
                          height: "18px",
                        }}
                        size="small"
                        onClick={() => handleExpandClick(row.id)}
                      >
                        {expanded === row.id ? (
                          <ExpandLess fontSize="small" />
                        ) : (
                          <ExpandMore fontSize="small" />
                        )}
                      </IconButton>
                    </TableCell>

                    <TableCell>
                      <Typography
                        sx={{
                          border: "1px solid var(--color-gray)",
                          textAlign: "center",
                          fontWeight: 500,
                          fontSize: "12px",
                          color: "var(--color-booking-id)",
                          fontFamily: "Outfit",
                          whiteSpace: "nowrap",
                          textOverflow: "ellipsis",
                          overflow: "hidden",
                          padding: "2px 0px",
                          borderRadius: "3px",
                        }}
                      >
                        {row?.hajjPackageId}
                      </Typography>
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        padding: "4px",
                      }}
                    >
                      {row.type}
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        padding: "4px",
                      }}
                    >
                      {row.hajjPackageName}
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        padding: "4px",
                      }}
                    >
                      {row.departureDate ? moment(row.departureDate).format("DD-MMM-YYYY") : null}
                    </TableCell>


                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        padding: "4px",
                      }}
                    >
                      {row.returnDate ? moment(row.returnDate).format("DD-MMM-YYYY") : null}
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        padding: "4px",
                      }}
                    >
                      {row.totalDays}
                    </TableCell>

                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        padding: "4px",
                      }}
                    >
                      {row.totalPerson}
                    </TableCell>
                    <TableCell
                      sx={{
                        textAlign: "center",
                        fontWeight: 500,
                        fontSize: "12px",
                        color: "var(--color-gray)",
                        fontFamily: "Outfit",
                        padding: "4px",
                      }}
                    >
                      {row.status}
                    </TableCell>
                    <TableCell
                      sx={{
                        padding: "6px !important",
                        verticalAlign: "middle",
                        textAlign: "right",
                      }}
                    >
                      <IconButton
                        size="small"
                        onClick={() =>
                          navigate("/dashboard/hajj-package-ledger", {
                            state: {
                              id: row.id,
                            },
                          })
                        }
                      >
                        <img
                          src={Transactionicon}
                          alt="Transaction"
                          style={{ width: "12px" }}
                        />
                      </IconButton>

                      {/* Service-wise Vendor/User Payment & Profit-Loss report */}
                      <IconButton
                        size="small"
                        title="Service Wise Profit/Loss Report"
                        onClick={() => handleOpenServiceReport(row)}
                      >
                        <AssessmentIcon
                          sx={{ fontSize: "16px", color: "var(--color-draft)" }}
                        />
                      </IconButton>

                      <IconButton
                        size="small"
                        onClick={(event) => {
                          setAnchorEl(event.currentTarget);
                          handleShow({ type: "action", payload: row });
                        }}
                      >
                        <img
                          src={EditMenu}
                          alt="Options"
                          style={{ width: "10px" }}
                        />
                      </IconButton>
                      <Menu
                        keepMounted
                        open={show.action}
                        anchorEl={anchorEl}
                        onClose={() => {
                          setAnchorEl(null);
                          handleClose("action");
                        }}
                      >
                        <MuiMenuItem
                          sx={{
                            color: "var(--color-gray)",
                            fontSize: "12px",
                            fontWeight: 300,
                            fontFamily: "Outfit",
                            "&:hover": {
                              backgroundColor:
                                "var(--color-bg-hover-sitebar)",
                              color: "var(--color-gray)",
                            },
                          }}
                          onClick={() => {
                            navigate(
                              `/dashboard/hajj-package-create/${data.id}`,
                            );
                          }}
                        >
                          Edit
                        </MuiMenuItem>

                        <MuiMenuItem
                          sx={{
                            color: "var(--color-unpaid)",
                            fontSize: "12px",
                            fontWeight: 300,
                            fontFamily: "Outfit",
                            "&:hover": {
                              backgroundColor: "var(--color-bg-hover-sitebar)",
                              color: "var(--color-gray)",
                            },
                          }}
                          onClick={handleDelete}
                        >
                          Delete
                        </MuiMenuItem>
                      </Menu>
                    </TableCell>
                  </TableRow>

                  <TableRow>
                    <TableCell
                      style={{
                        paddingBottom: 0,
                        paddingTop: 0,
                        backgroundColor: "var(--color-bg-hover-sitebar)",
                      }}
                      colSpan={9}
                    >
                      <Collapse
                        in={expanded === row.id}
                        timeout="auto"
                        unmountOnExit
                      >
                        <Box py={1}>
                          <Grid container spacing={0.5}>
                            <Grid item xs={12} sm={12} md={12}>
                              <Box sx={{ display: "flex" }}>
                                <Typography
                                  sx={{
                                    color: "var(--color-gray)",
                                    fontFamily: "Outfit",
                                    fontWeight: 400,
                                    fontSize: "12px",
                                    width: "fit-content",
                                    padding: "2px 10px",
                                    borderRadius: "10px",
                                  }}
                                >
                                  Created:
                                  <span
                                    style={{
                                      color: "var(--color-primary)",
                                      fontFamily: "Outfit",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {" "}
                                    {row?.createdByUser?.firstname}{" "}
                                    {row?.createdByUser?.lastname},{" "}
                                    {
                                      row?.createdByUser
                                        ?.identificationShortCode
                                    }{" "}
                                    {row.createdDateTime
                                      ? moment(row.createdDateTime).format(
                                        "DD-MMM-YYYY hh:mm",
                                      )
                                      : null}
                                  </span>
                                </Typography>

                                {row?.updatedByUser?.firstname ? (
                                  <Typography
                                    sx={{
                                      color: "var(--color-gray)",
                                      fontFamily: "Outfit",
                                      fontWeight: 400,
                                      fontSize: "12px",
                                      width: "fit-content",
                                      padding: "2px 10px",
                                      borderRadius: "10px",
                                    }}
                                  >
                                    Updated:{" "}
                                    <span
                                      style={{
                                        color: "var(--color-primary)",
                                        fontFamily: "Outfit",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {" "}
                                      {row?.updatedByUser?.firstname}{" "}
                                      {row?.updatedByUser?.lastname},{" "}
                                      {
                                        row?.updatedByUser
                                          ?.identificationShortCode
                                      }{" "}
                                      {row.updatedDateTime
                                        ? moment(row.updatedDateTime).format(
                                          "DD-MMM-YYYY hh:mm",
                                        )
                                        : null}
                                    </span>
                                  </Typography>
                                ) : null}
                                {row?.approvedByUser?.firstname ? (
                                  <Typography
                                    sx={{
                                      color: "var(--color-gray)",
                                      fontFamily: "Outfit",
                                      fontWeight: 400,
                                      fontSize: "12px",
                                      width: "fit-content",
                                      padding: "2px 10px",
                                      borderRadius: "10px",
                                    }}
                                  >
                                    Approved:{" "}
                                    <span
                                      style={{
                                        color: "var(--color-primary)",
                                        fontFamily: "Outfit",
                                        fontWeight: 500,
                                      }}
                                    >
                                      {" "}
                                      {row?.approvedByUser?.firstname}{" "}
                                      {row?.approvedByUser?.lastname},{" "}
                                      {
                                        row?.approvedByUser
                                          ?.identificationShortCode
                                      }{" "}
                                      {row.approvedDateTime
                                        ? moment(row.approvedDateTime).format(
                                          "DD-MMM-YYYY hh:mm",
                                        )
                                        : null}
                                    </span>
                                  </Typography>
                                ) : null}
                              </Box>
                            </Grid>

                            <Grid item xs={12} sm={12} md={12}>
                              <Box sx={{ display: "flex" }}>
                                <Typography
                                  sx={{
                                    color: "var(--color-gray)",
                                    fontFamily: "Outfit",
                                    fontWeight: 400,
                                    fontSize: "12px",
                                    width: "fit-content",
                                    padding: "2px 10px",
                                    borderRadius: "10px",
                                  }}
                                >
                                  Remarks:
                                  <span
                                    style={{
                                      color: "var(--color-primary)",
                                      fontFamily: "Outfit",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {" "}
                                    {row?.remarks}
                                  </span>
                                </Typography>
                              </Box>
                            </Grid>
                          </Grid>
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                </React.Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {charges.length ? (
          <Box
            sx={{
              width: "100%",
              mt: 2,
              display: "flex",
              justifyContent: "center",
            }}
          >
            <ThemeProvider theme={PaginationTheme}>
              <Stack spacing={2}>
                <Pagination
                  count={pageCount}
                  page={pageNumber}
                  onChange={handlePageChange}
                  color="primary"
                />
              </Stack>
            </ThemeProvider>
          </Box>
        ) : null}
      </Box>

      {/* ── Service-wise Vendor/User Payment & Profit-Loss Modal ── */}
      <Dialog
        open={reportOpen}
        onClose={handleCloseServiceReport}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            fontFamily: "Outfit",
            fontSize: "16px",
            fontWeight: 500,
          }}
        >
          Service Wise Profit / Loss{reportPackageName ? ` — ${reportPackageName}` : ""}
          <IconButton size="small" onClick={handleCloseServiceReport}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers>
          {reportLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", padding: "40px" }}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "var(--color-bg-hover-sitebar)" }}>
                    <TableCell sx={{ fontFamily: "Outfit", fontWeight: 500, fontSize: "12px" }}>
                      Service
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Outfit", fontWeight: 500, fontSize: "12px", textAlign: "right" }}>
                      Vendor Payment
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Outfit", fontWeight: 500, fontSize: "12px", textAlign: "right" }}>
                      User Payment
                    </TableCell>
                    <TableCell sx={{ fontFamily: "Outfit", fontWeight: 500, fontSize: "12px", textAlign: "right" }}>
                      Profit / Loss
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportRows.map((r) => (
                    <TableRow key={r.service}>
                      <TableCell sx={{ fontFamily: "Outfit", fontSize: "12px" }}>
                        {r.service}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Outfit", fontSize: "12px", textAlign: "right" }}>
                        {commaNumber(r.vendorPayment.toFixed(2))}
                      </TableCell>
                      <TableCell sx={{ fontFamily: "Outfit", fontSize: "12px", textAlign: "right" }}>
                        {commaNumber(r.userPayment.toFixed(2))}
                      </TableCell>
                      <TableCell
                        sx={{
                          fontFamily: "Outfit",
                          fontSize: "12px",
                          textAlign: "right",
                          fontWeight: 500,
                          color:
                            r.profitLoss > 0
                              ? "var(--color-paid)"
                              : r.profitLoss < 0
                                ? "var(--color-danger)"
                                : "var(--color-gray)",
                        }}
                      >
                        {commaNumber(r.profitLoss.toFixed(2))}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
                <TableFooterTotals reportTotals={reportTotals} />
              </Table>
            </TableContainer>
          )}
        </DialogContent>

        <DialogActions>
          <button
            onClick={handleCloseServiceReport}
            style={{
              backgroundColor: "var(--color-bg-hover-sitebar)",
              color: "var(--color-gray)",
              borderRadius: "5px",
              padding: "8px 16px",
              fontFamily: "Outfit",
              fontSize: "13px",
              fontWeight: 500,
              border: "none",
              cursor: "pointer",
            }}
          >
            Close
          </button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

// ---------------------------------------------------------------------------
// Small footer subcomponent for the report modal's grand totals row
// ---------------------------------------------------------------------------

const TableFooterTotals = ({ reportTotals }) => (
  <TableBody>
    <TableRow sx={{ backgroundColor: "var(--color-bg-hover-sitebar)" }}>
      <TableCell sx={{ fontFamily: "Outfit", fontSize: "12px", fontWeight: 700 }}>
        Total
      </TableCell>
      <TableCell sx={{ fontFamily: "Outfit", fontSize: "12px", fontWeight: 700, textAlign: "right" }}>
        {commaNumber(reportTotals.totalVendorPayment.toFixed(2))}
      </TableCell>
      <TableCell sx={{ fontFamily: "Outfit", fontSize: "12px", fontWeight: 700, textAlign: "right" }}>
        {commaNumber(reportTotals.totalUserPayment.toFixed(2))}
      </TableCell>
      <TableCell
        sx={{
          fontFamily: "Outfit",
          fontSize: "12px",
          fontWeight: 700,
          textAlign: "right",
          color:
            reportTotals.totalProfitLoss > 0
              ? "var(--color-paid)"
              : reportTotals.totalProfitLoss < 0
                ? "var(--color-danger)"
                : "var(--color-gray)",
        }}
      >
        {commaNumber(reportTotals.totalProfitLoss.toFixed(2))}
      </TableCell>
    </TableRow>
  </TableBody>
);

export default HajjPackageList;
